import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, Share2, Copy, X, Check } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

function groupByMonth(items) {
    const groups = {};
    items.forEach(c => {
        const d = new Date(c.updated_at || c.created_at);
        const key = d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
        groups[key] = groups[key] || [];
        groups[key].push(c);
    });
    return groups;
}

export default function History() {
    const [convs, setConvs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [shareModal, setShareModal] = useState(null);
    const [shareUrl, setShareUrl] = useState("");
    const [copied, setCopied] = useState(false);

    const load = async () => {
        try {
            const r = await api.get("/conversations");
            setConvs(r.data);
        } catch {
            toast.error("couldn't load history");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const remove = async (id) => {
        try {
            await api.delete(`/conversations/${id}`);
            setConvs(c => c.filter(x => x.id !== id));
            toast.success("removed");
        } catch {
            toast.error("couldn't remove");
        }
    };

    const openShare = async (conv) => {
        setShareModal(conv);
        setShareUrl("");
        setCopied(false);
        try {
            const { data } = await api.post(`/conversations/${conv.id}/share`);
            setShareUrl(`${window.location.origin}${data.share_url_path}`);
        } catch {
            toast.error("couldn't create share link");
            setShareModal(null);
        }
    };

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2200);
        } catch {
            toast.error("couldn't copy");
        }
    };

    const revokeShare = async () => {
        try {
            await api.delete(`/conversations/${shareModal.id}/share`);
            toast.success("link revoked");
            setShareModal(null);
        } catch {
            toast.error("couldn't revoke");
        }
    };

    const downloadMarkdown = async () => {
        try {
            const { data } = await api.get(`/conversations/${shareModal.id}/messages`);
            const lines = [
                `# ${shareModal.title || "conversation with pax"}`,
                "",
                `*${new Date(shareModal.created_at).toLocaleString()}*`,
                "",
                "> This is a conversation between a person and Pax, an AI companion. It is not a clinical assessment.",
                "",
            ];
            data.forEach(m => {
                lines.push(`**${m.role === "user" ? "Me" : "Pax"}** · ${new Date(m.created_at).toLocaleString()}`);
                lines.push("");
                lines.push(m.content);
                lines.push("");
            });
            const blob = new Blob([lines.join("\n")], { type: "text/markdown" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `pax-${(shareModal.title || "conversation").slice(0, 40).replace(/[^a-z0-9-]+/gi, "-").toLowerCase()}.md`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("downloaded");
        } catch {
            toast.error("download failed");
        }
    };

    const groups = groupByMonth(convs);

    return (
        <div className="max-w-3xl mx-auto">
            <h1 className="font-heading text-4xl tracking-tight text-ink mb-2">our conversations</h1>
            <p className="text-inkSoft font-body mb-10">every thread we've shared. you can revisit them, share with a therapist, or remove.</p>

            {loading && <p className="text-inkSoft font-body italic">loading…</p>}
            {!loading && convs.length === 0 && (
                <div className="bg-white/70 border border-clayHover rounded-3xl p-10 text-center">
                    <p className="font-journal text-ink italic mb-4">nothing here yet.</p>
                    <Link to="/companion" className="text-terracotta hover:underline font-body">begin a conversation →</Link>
                </div>
            )}

            <div className="space-y-10" data-testid="history-list">
                {Object.entries(groups).map(([month, items]) => (
                    <div key={month}>
                        <h2 className="text-xs uppercase tracking-[0.3em] text-inkSoft mb-4">{month}</h2>
                        <ul className="space-y-2">
                            {items.map(c => (
                                <li key={c.id} className="group flex items-center justify-between bg-white/60 hover:bg-white border border-clayHover rounded-2xl px-5 py-4 transition-colors">
                                    <Link to={`/companion/${c.id}`} className="flex-1 min-w-0" data-testid={`history-item-${c.id}`}>
                                        <p className="font-body text-ink truncate">{c.title || "untitled"}</p>
                                        <p className="text-xs text-inkSoft mt-1">
                                            {new Date(c.updated_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                                        </p>
                                    </Link>
                                    <div className="flex items-center gap-2 md:opacity-0 md:group-hover:opacity-100 transition-opacity ml-4">
                                        <button
                                            data-testid={`history-share-${c.id}`}
                                            onClick={() => openShare(c)}
                                            className="text-inkSoft hover:text-terracotta"
                                            aria-label="Share"
                                        >
                                            <Share2 className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => remove(c.id)}
                                            className="text-inkSoft hover:text-danger"
                                            data-testid={`history-delete-${c.id}`}
                                            aria-label="Delete"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>

            {shareModal && (
                <div
                    className="fixed inset-0 z-50 bg-ink/30 backdrop-blur-sm flex items-center justify-center p-6"
                    onClick={() => setShareModal(null)}
                    data-testid="share-modal"
                >
                    <div
                        className="bg-bone border border-clayHover rounded-3xl p-8 w-full max-w-md shadow-clay"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="flex items-start justify-between mb-2">
                            <div>
                                <h2 className="font-heading text-2xl tracking-tight text-ink">share this conversation</h2>
                                <p className="text-sm text-inkSoft font-body mt-1">with a therapist, or just to keep for yourself.</p>
                            </div>
                            <button onClick={() => setShareModal(null)} className="text-inkSoft hover:text-ink" data-testid="share-modal-close">
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="my-6">
                            <p className="text-xs uppercase tracking-widest text-inkSoft mb-2">read-only link</p>
                            <div className="flex items-center gap-2 bg-white border border-clayHover rounded-2xl px-3 py-2">
                                <input
                                    data-testid="share-url-input"
                                    readOnly
                                    value={shareUrl || "creating link…"}
                                    className="flex-1 bg-transparent outline-none font-body text-sm text-ink truncate"
                                />
                                <button
                                    data-testid="share-copy-btn"
                                    onClick={copyLink}
                                    disabled={!shareUrl}
                                    className="text-inkSoft hover:text-terracotta disabled:opacity-40"
                                    aria-label="Copy"
                                >
                                    {copied ? <Check className="w-4 h-4 text-sage" /> : <Copy className="w-4 h-4" />}
                                </button>
                            </div>
                            <p className="text-xs text-inkSoft mt-2 font-body">anyone with this link can read the conversation (no auth).</p>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <button
                                data-testid="share-download-md"
                                onClick={downloadMarkdown}
                                className="bg-white border border-clayHover rounded-full py-2.5 text-sm hover:bg-clay font-body text-ink"
                            >
                                download as markdown
                            </button>
                            <button
                                data-testid="share-revoke"
                                onClick={revokeShare}
                                className="bg-white border border-clayHover rounded-full py-2.5 text-sm hover:bg-clay font-body text-inkSoft"
                            >
                                revoke link
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
